import React, { useCallback, useEffect, useRef, useState } from "react";
import Styles from "./CreateScreen.module.css";
import DamStrip from "../../Modals/ConGen/Connection/DamStrip";
import { Add } from "@mui/icons-material";
import TenderBox from "../TenderBox/TenderBox";
import CreateTender from "../../Modals/ReverseAuction/CreateTender/CreateTender";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getequisitionsBidders,
  getequisitionsList,
} from "../../../Services/ReverseAuction/GetApi";
import {
  auctionBidders,
  auctionGet,
  setActiveView,
} from "../../../Redux/Slices/auctionSlice";
import { CircularProgress } from "@mui/material";
import emptyAvailableListing from "../../../assets/images/emptyAvailableListing.svg";
import SelectParticipant from "../../Modals/ReverseAuction/SelectParticipants/SelectParticipant";

const CreateScreen = ({ rfq }) => {
  const userType = useSelector((state) => state?.persistedReducer?.userType);
  const showDamStrip = userType === "CONSUMER" || userType === "GENERATOR";
  const auctionList = useSelector((state) => state?.auctionSlice?.auctionGet);
  const auctionSlice = useSelector((state) => state?.auctionSlice);
  const totalPages = useSelector(
    (state) => state?.auctionSlice?.auctionTotalPages,
  );
  const activeState = useSelector((state) => state?.auctionSlice?.activeView);
  const notify = useSelector((state) => state?.notificationSlice?.latest);
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);

  const isNotifyConsumerBidUpdated =
    notify?.key === "BSRANotifyConsumerBidCreated";

  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  ).toLowerCase();
  const [extended, setExtended] = useState("");
  const [reqList, setReqList] = useState([]);
  const [selectedBid, setSelectedBid] = useState([]);
  const [hoverId, setHoverId] = useState(null);
  const [tenderModal, setTenderModal] = useState(false);

  // Debug logging for pagination
  useEffect(() => {}, [page, totalPages, auctionList]);

  const sectionMap = {
    All: "All",
    Drafted: "Drafted",
    Bidding: "IPO",
    IPO: "IPO",
    "Live RA": "live_ra",
    Documentation: "TERMS",
    "Ongoing Contract": "CLOSED",
  };

  useEffect(() => {
    if (!activeState) return;
    const section = sectionMap[activeState] || "All";
    if (role === "consumer") {
      dispatch(auctionGet({ section, tenderType: rfq ? "CB" : "RA", page }));
    } else if (role === "generator") {
      dispatch(
        auctionBidders({ section, tenderType: rfq ? "CB" : "RA", page }),
      );
    }
  }, [page, activeState]); // eslint-disable-line react-hooks/exhaustive-deps

  const getReq = () => {
    getReqDetails(
      auctionList[0]?.tenderStatus === "DRAFTED" ||
        auctionList[0]?.tenderStatus === "ACTIVE_BID"
        ? ["bid_ongoing"]
        : ["ra_ongoing", "tender_closing_soon", "closed"],
      extended,
      "",
      false,
    );
  };

  // Define the simple triggers that don't require specific role/ID checks
  const REFRESH_TRIGGERS = [
    "BSRANotifyInvitedGenerator",
    "BSRANotifyConsumerTenderCreated",
    "BSRANotifyConsumerTenderTerminated",
    "BSRANotifyConsumerTenderUpdate",
    "BSRANotifyConsumerBidCreated",
    "BSRANotifyBidWinnerGenerator",
    "BSRANotifyBidRejectedGenerator",
    "BSRANotifyConsumerBidUpdated",
    "BSRANotifyBidRejectedGeneratorStageOne",
    "BSRANotifyBidRejectedGeneratorStageTwo",
    "BSRANotifyGeneratorBidCreated",
    "BSRANotifyConsumerTenderActivation",
    "BSRANotifyGeneratorBidUpdated",
    "BSRANotifyConsumerTenderStart",
    "BSRANotifyConsumerTenderEnd",
    "BSRANotifyGeneratorTenderStart",
    "BSRANotifyGeneratorTenderEnd",
    "BSRANotifyConsumerBidStart",
    "BSRANotifyConsumerBidEnd",
    "BSRANotifyGeneratorBidStart",
    "BSRANotifyGeneratorBidEnd",
    "BSRANotifyConsumerStartAuction",
    "BSRANotifyConsumerEndAuction",
    "BSRANotifyGeneratorStartAuction",
    "BSRANotifyGeneratorEndAuction",
    "BSRANotifyTenderActivationAllGenerator",
    "BSRANotifyAmountChangeRequestConsumer",
    "BSRANotifyAmountChangeResponseGenerator",
    "BSRANotifyBidWinnerConsumer",
    "BSRAEmdBlockedNotification",
    "BSRAEmdReleasedNotification",
    "BSRAConsumerRaFeeBlockedNotification",
    "BSRAGenerateConsent",
    "BSRFQNotifyConsumerTenderCreated",
  ];

  useEffect(() => {
    const key = notify?.key;

    // 1. Handle state reset
    // if (key === "BSRANotifyAmountChangeRequestGenerator") {
    //   setExtended("");
    // }

    // 2. Guard clause: If extended is empty/null, we don't need to proceed with getReq
    if (!extended || !key) return;

    // 3. Check for simple triggers
    const isStandardTrigger = REFRESH_TRIGGERS.includes(key);

    // 4. Check for complex triggers (dependent on Role and ID)
    const isConsumerMatch =
      key === "BSRANotifyAmountChangeResponseConsumer" &&
      role === "consumer" &&
      notify?.data?.tenderId === extended;

    const isGeneratorMatch =
      (key === "BSRANotifyBidWinnerGeneratorStageOne" ||
        key === "BSRANotifyBidWinnerGeneratorStageTwo") &&
      role === "generator" &&
      notify?.data?.tenderId === extended;

    const isGenBidReq = key === "BSRANotifyAmountChangeRequestGenerator";

    // 5. Execute if any condition matches
    if (
      isStandardTrigger ||
      isConsumerMatch ||
      isGeneratorMatch ||
      isGenBidReq
    ) {
      getReq();
    }
  }, [notify, extended, role]);

  const handleReq = (status, id, direct, selectedBid) => {
    if (extended === id) setExtended("");
    else {
      getReqDetails(status, id, direct, selectedBid);
      setExtended(id);
    }
  };

  const handleHover = (id) => {
    setHoverId(id);
  };

  const getReqDetails = async (status, id, direct, selectedBid) => {
    let stage = rfq
      ? "CB"
      : status?.includes("ra_ongoing") ||
          status?.includes("tender_closing_soon") ||
          status?.includes("closed")
        ? "AUCTION"
        : "CB";
    if (role === "consumer") {
      let res = await getequisitionsList(id, stage);
      if (res?.data?.code === 200) {
        if (direct === "edit_case") return res?.data?.data;

        if (selectedBid) {
          let bidMain = res?.data?.data
            ?.flatMap((req) => req.bids || [])
            ?.filter((ele) => ele?.winnerBidStatus === "ACCEPTED")
            ?.map((bid) => bid?.id);
          setSelectedBid(bidMain);
        }
        setReqList(res?.data?.data);
      }
    } else if (role === "generator") {
      let res = await getequisitionsBidders(id, stage);
      if (res?.data?.code === 200) {
        if (direct === "edit_case") return res?.data?.data;
        setReqList(res?.data?.data);
      }
    }
  };

  return (
    <>
      {tenderModal && (
        <CreateTender
          open={tenderModal}
          handleClose={() => setTenderModal(false)}
          type={"create"}
          listingMode={rfq ? "CB" : "RA"}
          rfq={rfq}
        />
      )}
      <div className={Styles.outerWrapper}>
        {showDamStrip && <DamStrip />}
        <div className={Styles.commonTenderModal}>
          <div className={`display_flex_row ${Styles.listingHeader}`}>
            <TenderHeader
              length={auctionList?.length}
              rfq={rfq}
              page={page}
              setPage={setPage}
            />
          </div>
          {(role === "consumer" && auctionSlice?.auctionGet === null) ||
          (role === "generator" && auctionSlice?.auctionGet === null) ? (
            <div className={Styles.circularProgress}>
              <CircularProgress enableTrackSlot size="30px" />
            </div>
          ) : (role === "consumer" && auctionSlice?.auctionGet?.length === 0) ||
            (role === "generator" && auctionSlice?.auctionGet?.length === 0) ? (
            <div className={Styles.emptyState}>
              <img src={emptyAvailableListing} alt="No Tender" />
              <div style={{ color: "var(--primary-color-text)" }}>
                {role === "consumer" ? (
                  <span>
                    You have not listed your <b>requirements</b>
                  </span>
                ) : (
                  <span>
                    No Active <b>requirements</b>
                  </span>
                )}
              </div>
              {role === "consumer" && (
                <div
                  className={`activeButton_type_2 ${Styles.addButton}`}
                  onClick={() => setTenderModal(true)}
                >
                  <Add style={{ fontSize: "16px" }} />
                  <span>Add Tender</span>
                </div>
              )}
            </div>
          ) : (
            <>
              <div
                className={`scroll_bar_y display_flex_column_gap_8 ${Styles.tenderDetails}`}
              >
                {auctionList?.map((ele, index) => (
                  <TenderBox
                    key={ele?.id || index}
                    data={ele}
                    reqExt={extended}
                    handleReq={handleReq}
                    getReqDetails={getReqDetails}
                    reqList={reqList}
                    selectedBid={selectedBid}
                    hoverId={hoverId}
                    handleHover={handleHover}
                    rfq={rfq}
                  />
                ))}
              </div>
              {totalPages > 1 && (
                <div
                  className={`display_flex_row_center ${Styles.paginationContainer}`}
                >
                  <button
                    className={Styles.paginationBtn}
                    disabled={page === 0}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Prev
                  </button>
                  <span className={Styles.paginationInfo}>
                    {page + 1} / {totalPages}
                  </span>
                  <button
                    className={Styles.paginationBtn}
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                    title={`Current page: ${page + 1}, Total pages: ${totalPages}`}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

const TenderHeader = ({ length, rfq, page, setPage }) => {
  const dispatch = useDispatch();
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  ).toLowerCase();
  const [tenderModal, setTenderModal] = useState(false);
  const activeState = useSelector((state) => state?.auctionSlice?.activeView);
  const prevActiveStateRef = useRef(activeState);

  const FilterName = window.location.pathname.includes(
    "/rfq" || "/tradeconnect",
  )
    ? "Bidding"
    : "IPO";

  useEffect(() => {
    if (activeState === null || activeState === undefined) {
      dispatch(setActiveView("All"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAuction = useCallback(
    (value) => {
      if (activeState === value) return;
      setPage(0);
      dispatch(setActiveView(value));
    },
    [activeState, dispatch, setPage],
  );

  // Track activeState changes for debugging
  useEffect(() => {
    if (prevActiveStateRef.current !== activeState) {
      prevActiveStateRef.current = activeState;
    }
  }, [activeState]);

  return (
    <>
      {tenderModal && (
        <CreateTender
          open={tenderModal}
          handleClose={() => setTenderModal(false)}
          type={"create"}
          listingMode={rfq ? "CB" : "RA"}
          rfq={rfq}
        />
      )}
      <div className={`display_flex_row ${Styles.listingHeaderList}`}>
        <div
          className={`display_flex_row_gap_8_only`}
          style={{ gap: role === "consumer" ? "32px" : undefined }}
        >
          <div className={Styles.myListing}>
            {role === "consumer" ? "My Tender" : "Tender List"}
          </div>
          {role === "consumer" ? (
            <div
              className={`activeButton_type_2 ${Styles.addButton}`}
              onClick={() => setTenderModal(true)}
            >
              <Add style={{ fontSize: "16px" }} />
              <span>Add Tender</span>
            </div>
          ) : (
            <div>: {length || 0}</div>
          )}
        </div>
        <div
          className={`display_flex_row_gap_4_only ${Styles.listingHeaderSelectionList}`}
        >
          {(role === "generator"
            ? [
                "All",
                FilterName,
                "Live RA",
                "Documentation",
                "Ongoing Contract",
              ]
            : [
                "All",
                "Drafted",
                FilterName,
                "Live RA",
                "Documentation",
                "Ongoing Contract",
              ]
          )
            .filter((ele) => !(rfq && ele === "Live RA"))
            .map((ele) => (
              <div
                key={ele}
                className={`display_flex_row ${activeState === ele ? Styles.activeButton : ""}`}
                onClick={() => {
                  getAuction(ele);
                }}
              >
                {ele}
              </div>
            ))}
        </div>
      </div>
    </>
  );
};
export default CreateScreen;
